import { Dispatch, FC, Fragment, SetStateAction, useEffect, useMemo, useState } from 'react';

import { CloseOutlined, DownOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Input, Popover, Tree, Typography } from 'antd';
import { useLocation } from 'react-router-dom';

import FilterItemSelected from './FilterItemSelected';
import { getStartEndType } from './pickers/DateTimeRangePicker';
import RefreshButton from './RefreshButton';
import RequestButton from './RequestButton';
import Segments from './segments';
import { PagesPathnames } from '../../../../../../app/types/ad';
import {
  findObjectById,
} from '../../../../../../helpers/utils/functions';
import {
  generateListKeys,
  getParentKey,
  prepareData,
} from '../../../../../../helpers/utils/reports';
import {
  calendarTypes,
  data,
  updateFilters,
} from '../../../../constants/filters';
import { routeNames } from '../../../../constants/routes';
import { useActions } from '../../../../hooks/useActions';
import { useTypedSelector } from '../../../../hooks/useTypedSelector';

interface IUniversalFilterProps {
  isCompare: boolean;
  withSegment: boolean;
  disabled?: boolean;
  disableAttr?: boolean;
  filtersFilter?: string[];
  withLabel?: boolean;
  notParams?: boolean;
  withRefresh?: boolean;
  reqBtnDisabled?: boolean;
  setReqBtnDisabled?: Dispatch<SetStateAction<boolean>>
}

export const listKeys = generateListKeys(data);

const UniversalFilter: FC<IUniversalFilterProps> = ({
  isCompare,
  withSegment,
  filtersFilter,
  disabled = false,
  disableAttr = false,
  withLabel = true,
  notParams = false,
  withRefresh = false,
  setReqBtnDisabled,
  reqBtnDisabled = true,
}) => {
  const { isNotParamsFilterVisible } = useTypedSelector((state) => state.meta);
  const { filters, filterSelectorVisible, filterParent } = useTypedSelector(
    (state) => state.filter
  );
  const locationState = useLocation();

  const {
    setMetaCompare,
    setFilterParent,
    setFilters,
    setCurrentSegment,
    setAllDataFetching,
    setMetaFilterUpdate,
    setAllBlocksFetching,
    setCurrentFunnel,
  } = useActions();
  const [ expandedKeys, setExpandedKeys ] = useState<string[]>([]);
  const [ searchValue, setSearchValue ] = useState('');
  const [ autoExpandParent, setAutoExpandParent ] = useState(true);
  const [ treeData, setTreeData ] = useState<any>([]);
  const [ filterVisible, setFilterVisible ] = useState(false);

  useEffect(() => {
    let data_ = data;
    if (location.pathname === '/users/ad-reports') {
      data_ = updateFilters();
    }

    if (location.pathname === '/webinar/quality') {
      data_ = updateFilters().map(
        (filter: { children: { name: string }[] }) => {
          return {
            ...filter,
            children: filter.children.filter(
              (childFilter: { name: string }) =>
                !childFilter.name.startsWith('Период')
            ),
          };
        }
      );
    }

    const filteredData = filtersFilter
      ? data_.filter((item: any) => filtersFilter.includes(item.name))
      : data_;
    const _data = prepareData(
      filteredData,
      searchValue,
      filters,
      disableAttr,
      '',
      '',
      '',
      '',
      [ '' ],
      '',
      filterParent,
      handleActivateReq
    );

    if (_data.length === 1) {
      setAutoExpandParent(true);
      setExpandedKeys([ '0-0' ]);
    }

    setTreeData(_data);
  }, [
    data,
    searchValue,
    filters,
    filters?.length,
    isCompare,
    filterParent,
    location.pathname,
    filtersFilter,
  ]);

  useEffect(() => {
    if (!searchValue) {
      let data_ = data;
      if (location.pathname === '/users/ad-reports') {
        data_ = updateFilters();
      }
      setExpandedKeys([]);
      setAutoExpandParent(false);
      const filteredData = filtersFilter
        ? data_.filter((item: any) => filtersFilter.includes(item.name))
        : data_;
      const _data = prepareData(
        filteredData,
        searchValue,
        filters,
        disableAttr,
        '',
        '',
        '',
        '',
        [ '' ],
        '',
        filterParent,
        handleActivateReq
      );

      setTreeData(_data);
    }
  }, [ searchValue ]);

  useEffect(() => {
    if (!isCompare && !notParams) {
      setMetaCompare({ value: false });
    }
  }, [ isCompare ]);

  useEffect(() => {
    if (!filterSelectorVisible) {
      setExpandedKeys([]);
    }
  }, [ filterSelectorVisible ]);

  const onExpand = (_expandedKeys: any) => {
    setExpandedKeys(_expandedKeys);
    setAutoExpandParent(false);
  };

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value !== '') {
      const reg = new RegExp(value, 'i');
      const _treeData = treeData;

      let _expandedKeys: any = listKeys
        .map((item: any) => {
          if (item.name.search(reg) > -1) {
            return getParentKey(item.id, _treeData);
          }

          return null;
        })
        .filter(
          (item: any, i: number, self: any) => item && self.indexOf(item) === i
        );

      setExpandedKeys(_expandedKeys);
      setSearchValue(value);
      setAutoExpandParent(true);
    } else {
      setExpandedKeys([]);
      setSearchValue('');
      setAutoExpandParent(false);
    }
  };
  const onClearFilters = () => {
    setCurrentSegment(null);
    setCurrentFunnel([]);
    setFilters([]);
    setFilterParent([]);
  };

  const handleUpdateDashboardData = () => {
    setAllBlocksFetching({ value: true });
    setAllDataFetching({ value: true });
    setTimeout(() => {
        setAllBlocksFetching({ value: false });
        setMetaFilterUpdate({ value: false }); 
    }, 2000);
  };

  const handleActivateReq = (disable: boolean, withoutFilter = true) => {
    !withoutFilter && setFilterVisible(disable);
  };

  const isAutoRKTab =
    locationState.pathname === PagesPathnames.autoPlacement &&
    (Number(new URLSearchParams(location.search).get('tab')) === 1 ||
      !new URLSearchParams(location.search).get('tab'));

  const isAutoAdTab =
    locationState.pathname === PagesPathnames.autoPlacement &&
    Number(new URLSearchParams(location.search).get('tab')) === 2;

  const isManualAdTab =
    locationState.pathname === PagesPathnames.manualPlacement &&
    Number(new URLSearchParams(location.search).get('tab')) === 1;

  const filterRender = useMemo(() => 
    filters.map((item: any, index: number) => {
      if (item.type === 'datetimerange') {
        if (!item.start && !item.end) {
          return null;
        }
      }

      let prefix = 'и';
      let secondPrefix = null;
      const prevIndex = index === 0 ? 0 : index - 1;
      if (item.id === filters[prevIndex].id) {
        prefix = 'или';
      }

      if (item.id.includes('not_')) {
        secondPrefix = 'не';
      }

      return (
        <Fragment key={index}>
          {index !== 0 && (
            <Typography.Text type="secondary">
              {prefix}
            </Typography.Text>
          )}
          {secondPrefix ? (
            <b style={{ fontWeight: 600, color: '#ff0000' }}>
              {secondPrefix}
            </b>
          ) : (
            ''
          )}
          {
            <FilterItemSelected
              arrayIndex={index}
              id={item.id}
              itemData={item}
              full_name={item.full_name}
              {...item}
            />
          }
        </Fragment>
      );
    }),[ filters ]);
    
  return (
    <div
      className={`params__filter ${isAutoRKTab ? 'filter-auto-rk-tab' : ''}${
        isAutoAdTab ? 'filter-auto-ad-tab' : ''
      }${isManualAdTab ? 'filter-manual-ad-tab' : ''}`}
      style={{ paddingBottom: filters?.length > 0 ? '0px' : '20px' }}
    >
      <div>
        {withSegment ? (
          <Segments disabled={disabled} setBtnDisabled={handleActivateReq} />
        ) : null}
      </div>
      {withRefresh && <RefreshButton />}
      {filters?.length > 0 && (
        <Button
          style={{ marginRight: 5 }}
          onClick={() => onClearFilters()}
          icon={<CloseOutlined />}
          title="Очистить"
        />
      )}
      <div
        style={{
          display: 'inline',
          marginLeft: withSegment ? 10 : 0,
        }}
      >
        {withLabel && (
          <Typography.Text type="secondary">
            Условия фильтра:&nbsp;
          </Typography.Text>
        )}
       
        {filters?.length !== 0 &&
          filterRender
        }
        <Popover
          content={
            (notParams || !isNotParamsFilterVisible) && (
              <Tree.DirectoryTree
                blockNode
                switcherIcon={<DownOutlined />}
                showIcon={false}
                selectable={false}
                treeData={
                  disableAttr
                    ? treeData.filter(
                        (item: any) =>
                          item.name !== 'Реклама' && item.name !== 'Регистрации'
                      )
                    : treeData
                }
                onExpand={onExpand}
                expandedKeys={expandedKeys}
                autoExpandParent={autoExpandParent}
                key={'name'}
              />
            )
          }
          title={
            (notParams || !isNotParamsFilterVisible) && (
              <Input
                onChange={(e) => onSearch(e)}
                placeholder="Найти условие1"
                allowClear
              />
            )
          }
          trigger="click"
          placement="bottom"
          overlayClassName="filter-popover"
          open={filterVisible}
          onOpenChange={setFilterVisible}
        >
          <Button icon={<PlusOutlined />} type="primary" shape="circle" />
        </Popover>
        {!notParams && setReqBtnDisabled && (
          <RequestButton
            setUpdateDashboard={handleUpdateDashboardData}
            btnDisabled={reqBtnDisabled}
            setBtnDisabled={setReqBtnDisabled}
          />
        )}
      </div>
    </div>
  );
};

export default UniversalFilter;
